import {
  type ChartReviewResponse,
  type FeedbackRequest,
  newBlankFeedbackRequest,
} from '@ctw/shared/api/ai/types';
import { useFeedback } from '@ctw/shared/api/ai/use-feedback';
import {
  NotFoundSummaryHPI,
  useSummaryForPatient,
} from '@ctw/shared/api/ai/use-precompute-summary';

import useConfig from '@ctw/shared/api/ai/use-config';
import { Button } from '@ctw/shared/components/button';
import { ButtonGroup } from '@ctw/shared/components/button-group';
import { Card } from '@ctw/shared/components/card';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { NoDataError } from '@ctw/shared/components/errors/no-data-error';
import { FeedbackThumbs } from '@ctw/shared/components/feedback-thumbs';
import { Heading } from '@ctw/shared/components/heading';
import { TwoColumnContent } from '@ctw/shared/components/layout/two-column-content';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { usePatientSummaryDrawer } from '@ctw/shared/content/chart-review/patient-summary-drawer';
import { PrecomputedSummary } from '@ctw/shared/content/chart-review/precomputed-summary';
import { useSummaryConfigDrawer } from '@ctw/shared/content/chart-review/summary-config-drawer';
import {
  type OverviewResources,
  PatientOverview,
} from '@ctw/shared/content/overview/patient-overview';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faGear, faSidebar, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export interface PatientSummaryProps {
  className?: ClassName;
}

export const PatientSummary = withErrorBoundary({
  boundaryName: 'PatientSummary',
  Component: ({ className }: PatientSummaryProps) => {
    const { patient } = usePatientContext();
    const telemetry = useTelemetry();

    const useNewAIApi = useFeatureFlag('ctw-focused-summary');

    const { requestContext } = useCTW();

    const configs = useConfig(requestContext.builderId || '');

    const [displaySummaryType, setDisplaySummaryType] = useState<string | null>(null);

    // Effect to set the initial/default summary type once configs are loaded
    useEffect(() => {
      // If the new AI API is enabled, set the default summary type from the configs
      if (configs.configs && displaySummaryType === null) {
        if (useNewAIApi) {
          setDisplaySummaryType(configs.configs?.DefaultSummaryType ?? 'general');
        } else {
          setDisplaySummaryType('new_patient');
        }
      }
    }, [configs.configs, useNewAIApi, displaySummaryType]);

    const [isLoading, setIsLoading] = useState(false);
    // Track if we're manually fetching to avoid duplicate requests
    const isFetchingRef = useRef(false);

    const {
      document: hookDocument,
      getSummary,
      regenerateSummary,
      isGenerating,
    } = useSummaryForPatient(displaySummaryType);

    const [localDocument, setLocalDocument] = useState<ChartReviewResponse | undefined>(undefined);

    // Helper function to update summary type and clear local document
    const updateSummaryType = (type: string) => {
      setLocalDocument(undefined);
      setDisplaySummaryType(type);
    };

    // Memoize document to prevent unnecessary re-renders
    const document = useMemo(() => localDocument || hookDocument, [localDocument, hookDocument]);

    const [feedback, setFeedback] = useState<FeedbackRequest>(
      newBlankFeedbackRequest({ upid: patient.UPID }),
    );
    const { triggerFeedback } = useFeedback(feedback);

    const containerRef = useRef<HTMLDivElement>(null);
    const { breakpoints } = useContainerQuery({ containerRef });

    const { isDrawerOpen, openDrawer } = usePatientSummaryDrawer();

    const { isDrawerOpen: isSummaryConfigDrawerOpen, openDrawer: openSummaryConfigDrawer } =
      useSummaryConfigDrawer();

    const { navigate } = useZuiContext();
    const redirectTo = useCallback(
      (resource: OverviewResources) => {
        navigate(resource);
      },
      [navigate],
    );

    const [isEvidenceSetToVisible, setIsEvidenceSetToVisible] = useState(true);

    const handleGetSummary = () => {
      try {
        return getSummary(displaySummaryType);
      } catch (error) {
        return error;
      }
    };

    const handleOpenDrawerFromThumbs = (isThumbsUp: boolean) => {
      const newFeedback = { ...feedback, isThumbsUp };
      if (feedback.isThumbsUp !== isThumbsUp) {
        newFeedback.reasons = []; // Different set of reasons between thumbs up and down
      }
      openDrawer({
        feedback: newFeedback,
        setFeedback: setFeedback,
        triggerFeedback,
      });
    };

    const handleOpenSummaryConfigDrawer = () => {
      openSummaryConfigDrawer({
        builderId: requestContext.builderId,
      });
    };

    const handleRegenerateSummary = useCallback(async () => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const result = await regenerateSummary(displaySummaryType);
        if (result?.data) {
          setLocalDocument(result.data);
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        telemetry.trackError({
          message: 'Error regenerating summary',
          error: err,
        });
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, [regenerateSummary, displaySummaryType, telemetry]);

    return (
      <div ref={containerRef} className={tw`relative flex h-full flex-col px-2 pt-2`}>
        <Heading level="h1" className={tw`font-normal font-size-3xl`}>
          Your 5-Minute Briefing on {patient.displayFull}
        </Heading>
        {document?.hpi === NotFoundSummaryHPI ? (
          <NoDataError
            className={tw`my-4 rounded-lg bg-background-hover`}
            message="Sorry, we weren't able to generate a summary for this patient."
          />
        ) : (
          <TwoColumnContent
            leftColumn={{
              hidden: !(breakpoints.isAtLeast.md && isEvidenceSetToVisible),
              header: (
                <div className={tw`flex flex-1 items-center gap-2`}>
                  {breakpoints.isAtLeast.md && (
                    <button
                      type="button"
                      onClick={() => {
                        telemetry.trackInteraction('patient_summary_evidence_drawer_close');
                        setIsEvidenceSetToVisible(false);
                      }}
                      className={tw`flex h-4 w-4 items-center justify-center`}
                      aria-label="Close Evidence Drawer"
                    >
                      <FontAwesomeIcon
                        icon={faSidebar}
                        className={tw`text-content-icon hover:text-content-subtle`}
                      />
                    </button>
                  )}
                  <Heading
                    level="h2"
                    className={twx(
                      'line-clamp-2 flex items-center gap-2 text-content-subtle text-xs uppercase',
                    )}
                  >
                    Clinical Evidence
                  </Heading>
                </div>
              ),
              content: (
                <PatientOverview onSeeAllResources={redirectTo} className={tw`min-w-[320px]`} />
              ),
            }}
            rightColumn={{
              header: (
                <div className={tw`flex min-w-0 flex-1 justify-between gap-2`}>
                  <div className={tw`flex items-center gap-2`}>
                    {!isEvidenceSetToVisible && breakpoints.isAtLeast.md && (
                      <button
                        type="button"
                        onClick={() => {
                          telemetry.trackInteraction('patient_summary_evidence_drawer_open');
                          setIsEvidenceSetToVisible(true);
                        }}
                        className={tw`flex h-4 w-4 items-center justify-center`}
                      >
                        <FontAwesomeIcon
                          icon={faSidebar}
                          className={tw`text-content-icon hover:text-content-subtle`}
                        />
                      </button>
                    )}
                    <Heading
                      level="h2"
                      className={twx(
                        'line-clamp-2 flex w-fit items-center gap-2 text-content-subtle text-xs uppercase',
                      )}
                    >
                      Summary of all available information
                    </Heading>
                    <Tooltip
                      id="patient-summary-generated-time"
                      content={`Generated ${formatDateToLocalString(document?.createdAt)}`}
                    >
                      <FontAwesomeIcon
                        className={tw`block h-4 w-4 text-content-icon`}
                        icon={faInfoCircle}
                      />
                    </Tooltip>
                  </div>
                  <div className={twx('flex items-center gap-1')}>
                    {useNewAIApi && (
                      <ButtonGroup>
                        <Button
                          type="button"
                          disabled={!displaySummaryType}
                          variant={displaySummaryType === 'general' ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => updateSummaryType('general')}
                        >
                          General
                        </Button>
                        <Button
                          type="button"
                          disabled={!displaySummaryType}
                          variant={displaySummaryType === 'focused' ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => updateSummaryType('focused')}
                        >
                          Focused
                        </Button>
                      </ButtonGroup>
                    )}
                    <Button
                      variant="unstyled"
                      size="sm"
                      onClick={() => {
                        handleOpenDrawerFromThumbs(true);
                      }}
                      type="button"
                      aria-label="Thumbs up"
                      telemetryInteractionName="feedback_thumbs_up"
                      disabled={isDrawerOpen}
                    >
                      <FontAwesomeIcon
                        icon={faThumbsUp}
                        className={twx('text-content-icon transition-all duration-150', {
                          'hover:text-content-subtle': !isDrawerOpen,
                        })}
                      />
                    </Button>
                    <Button
                      variant="unstyled"
                      size="sm"
                      type="button"
                      aria-label="Thumbs down"
                      telemetryInteractionName="feedback_thumbs_down"
                      onClick={() => {
                        handleOpenDrawerFromThumbs(false);
                      }}
                      disabled={isDrawerOpen}
                    >
                      <FontAwesomeIcon
                        icon={faThumbsDown}
                        className={twx(
                          'scale-x-[-1] text-content-icon transition-all duration-150',
                          {
                            'hover:text-content-subtle': !isDrawerOpen,
                          },
                        )}
                      />
                    </Button>
                    {useNewAIApi && (
                      <>
                        <Button
                          variant="unstyled"
                          size="sm"
                          type="button"
                          ariaLabel="Summary Config"
                          onClick={handleOpenSummaryConfigDrawer}
                          disabled={isSummaryConfigDrawerOpen}
                        >
                          <FontAwesomeIcon icon={faGear} className={twx('text-content-icon')} />
                        </Button>
                        <Button
                          variant="unstyled"
                          size="sm"
                          type="button"
                          ariaLabel="Regenerate Summary"
                          onClick={handleRegenerateSummary}
                          disabled={isSummaryConfigDrawerOpen}
                        >
                          <FontAwesomeIcon icon={faRefresh} className={twx('text-content-icon')} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ), // Right margin for thumbs up/down buttons
              content: (
                <div className={twx('space-y-4')}>
                  <PrecomputedSummary
                    key={`summary-${displaySummaryType ?? 'loading'}`}
                    onGenerateSummary={handleGetSummary}
                    document={document}
                    isGenerating={isGenerating || isLoading || !displaySummaryType}
                    className={twx('rounded-lg bg-background-hover p-4', className)}
                  />
                  <Card
                    className={twx('p-2 transition-all duration-300', {
                      'invisible opacity-0': isDrawerOpen,
                      'opacity-100': !isDrawerOpen,
                    })}
                  >
                    <div className={tw`space-y-4`}>
                      <Heading level="h4" className={tw`font-medium text-2xl text-content-subtle`}>
                        What Did You Think of this Summary?
                      </Heading>
                      <FeedbackThumbs
                        onChange={handleOpenDrawerFromThumbs}
                        selectedStateDisabled={true}
                      />
                    </div>
                  </Card>
                </div>
              ),
            }}
          />
        )}
      </div>
    );
  },
});
